import React, { useState, useEffect } from 'react';
import LandingPage from './components/LandingPage';
import ChatInterface from './components/ChatInterface';
import MarketConfirmation from './components/MarketConfirmation';
import TreeVisualization from './components/TreeVisualization';
import RedditInsights from './components/RedditInsights';
import SegmentReport from './components/SegmentReport';
import ProgressBar from './components/ProgressBar';
import { getProgressData, saveToStorage, StorageKeys } from './utils/storage';
import { AuthProvider } from './utils/auth';

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [userIdea, setUserIdea] = useState('');
  const [identifiedMarket, setIdentifiedMarket] = useState('');
  const [confirmedMarket, setConfirmedMarket] = useState('');
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [redditAnalysis, setRedditAnalysis] = useState(null);

  useEffect(() => {
    const progressData = getProgressData();
    
    if (progressData.currentStep) {
      setCurrentStep(progressData.currentStep);
    }
    if (progressData.userIdea) {
      setUserIdea(progressData.userIdea);
    }
    if (progressData.confirmedMarket) {
      setConfirmedMarket(progressData.confirmedMarket);
      setIdentifiedMarket(progressData.confirmedMarket);
    }
    if (progressData.selectedSegments) {
      setSelectedSegments(progressData.selectedSegments);
    }
  }, []);

  const handleStartDiscovery = (idea) => {
    setUserIdea(idea);
    setCurrentStep(2);
    saveToStorage(StorageKeys.CURRENT_STEP, 2);
  };

  const handleMarketIdentified = (market) => {
    setIdentifiedMarket(market);
    setCurrentStep(3);
    saveToStorage(StorageKeys.CURRENT_STEP, 3);
  };

  const handleAcceptMarket = (market) => {
    setConfirmedMarket(market);
    setCurrentStep(4);
    saveToStorage(StorageKeys.CURRENT_STEP, 4);
  };

  const handleContinueRefining = () => {
    setCurrentStep(2);
    saveToStorage(StorageKeys.CURRENT_STEP, 2);
  };

  const handleGenerateReport = (segments) => {
    setSelectedSegments(segments);
    setCurrentStep(5); // Reddit insights step
    saveToStorage(StorageKeys.CURRENT_STEP, 5);
  };

  const handleRedditAnalysisComplete = (analysis) => {
    setRedditAnalysis(analysis);
    setCurrentStep(6); // Final report step
    saveToStorage(StorageKeys.CURRENT_STEP, 6);
  };

  const handleStartOver = () => {
    setCurrentStep(1);
    setUserIdea('');
    setIdentifiedMarket('');
    setConfirmedMarket('');
    setSelectedSegments([]);
    setRedditAnalysis(null);
    saveToStorage(StorageKeys.CURRENT_STEP, 1);
  };

  const handleResetToStart = () => {
    setCurrentStep(1);
    setUserIdea('');
    setIdentifiedMarket('');
    setConfirmedMarket('');
    setSelectedSegments([]);
    setRedditAnalysis(null);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <LandingPage onStartDiscovery={handleStartDiscovery} />;
      case 2:
        return (
          <ChatInterface 
            userIdea={userIdea} 
            onMarketIdentified={handleMarketIdentified}
            onResetToStart={handleResetToStart}
          />
        );
      case 3:
        return (
          <MarketConfirmation 
            identifiedMarket={identifiedMarket}
            onAcceptMarket={handleAcceptMarket}
            onContinueRefining={handleContinueRefining}
            onResetToStart={handleResetToStart}
          />
        );
      case 4:
        return (
          <TreeVisualization
            confirmedMarket={confirmedMarket}
            onGenerateReport={handleGenerateReport}
            onResetToStart={handleResetToStart}
          />
        );
      case 5:
        return (
          <RedditInsights
            selectedSegments={selectedSegments}
            confirmedMarket={confirmedMarket}
            onAnalysisComplete={handleRedditAnalysisComplete}
            onResetToStart={handleResetToStart}
          />
        );
      case 6:
        return (
          <SegmentReport
            selectedSegments={selectedSegments}
            confirmedMarket={confirmedMarket}
            redditAnalysis={redditAnalysis}
            onStartOver={handleStartOver}
            onResetToStart={handleResetToStart}
          />
        );
      default:
        return <LandingPage onStartDiscovery={handleStartDiscovery} />;
    }
  };

  return (
    <AuthProvider>
      <div className="App min-h-screen bg-gray-50">
        {currentStep > 1 && currentStep < 6 && (
          <ProgressBar currentStep={currentStep} />
        )}
        {renderCurrentStep()}
      </div>
    </AuthProvider>
  );
}

export default App;
